import React, { useState, useEffect, useCallback } from "react";
import {
  FiX,
  FiMapPin,
  FiDollarSign,
  FiCalendar,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiMessageSquare,
  FiAlertCircle,
  FiClock,
} from "react-icons/fi";
import {
  getCustomerLeadNotes,
  addCustomerLeadNote,
  editCustomerLeadNote,
  deleteCustomerLeadNote,
} from "../../../api/customer/customerFeatureApis";

const CustomerLeadDetailsModal = ({ isOpen, onClose, lead, token, onEditLead }) => {
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [actionError, setActionError] = useState(null);

  const fetchNotes = useCallback(async () => {
    if (!lead?.id || !token) return;
    setLoadingNotes(true);
    setActionError(null);
    try {
      const res = await getCustomerLeadNotes(token, lead.id);
      const list = res?.customerLeadNotes || res?.notes || [];
      setNotes(list);
    } catch (err) {
      setActionError("Unable to load notes for this lead.");
    } finally {
      setLoadingNotes(false);
    }
  }, [lead?.id, token]);

  useEffect(() => {
    if (isOpen && lead?.id) {
      fetchNotes();
      setNewNote("");
      setEditingNoteId(null);
    }
  }, [isOpen, lead?.id, fetchNotes]);

  if (!isOpen || !lead) return null;

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setAddingNote(true);
    setActionError(null);
    try {
      const res = await addCustomerLeadNote(token, {
        leadId: lead.id,
        note: newNote.trim(),
      });
      if (res?.statusType === "SUCCESS" || res?.customerLeadNote) {
        setNewNote("");
        fetchNotes();
      } else {
        setActionError(res?.text || "Failed to add note.");
      }
    } catch (err) {
      setActionError(err?.response?.data?.text || "Error adding note.");
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveEditNote = async (noteObj) => {
    if (!editingNoteText.trim()) return;
    try {
      const payload = {
        id: noteObj.id,
        note: editingNoteText.trim(),
        parent_id: noteObj.parent_id || 0,
        customerLead: { id: lead.id },
        user: { id: noteObj.user?.id },
      };
      await editCustomerLeadNote(token, payload);
      setEditingNoteId(null);
      setEditingNoteText("");
      fetchNotes();
    } catch (err) {
      setActionError("Failed to update note.");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await deleteCustomerLeadNote(token, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      setActionError("Failed to delete note.");
    }
  };

  const formatBudget = (num) => {
    if (!num) return "₹0";
    return `₹${Number(num).toLocaleString("en-IN")}`;
  };

  const purposeName = lead.purpose?.name || lead.purpose?.purpose_name || "General Requirement";

  return (
    <div className="customer-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="lead-details-title">
      <div className="customer-modal" style={{ maxWidth: "680px" }}>
        <div className="customer-modal__header">
          <div>
            <span className="customer-lead-card__purpose mb-1">{purposeName}</span>
            <h2 id="lead-details-title" className="customer-modal__title mt-1">
              {lead.title}
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light border-0"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="customer-modal__body">
          {actionError && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-0" role="alert">
              <FiAlertCircle className="flex-shrink-0" />
              <span className="small">{actionError}</span>
            </div>
          )}

          {/* Key details bar */}
          <div className="p-3 bg-light rounded-3 d-flex flex-wrap gap-4 align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FiDollarSign size={18} className="text-success" />
              <div>
                <div className="text-muted small">Budget</div>
                <div className="fw-bold text-success fs-6">{formatBudget(lead.budget)}</div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <FiMapPin size={18} className="text-primary" />
              <div>
                <div className="text-muted small">Location</div>
                <div className="fw-semibold text-dark fs-6">
                  {[lead.city, lead.state].filter(Boolean).join(", ") || "Not Specified"}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <FiCalendar size={18} className="text-muted" />
              <div>
                <div className="text-muted small">Created On</div>
                <div className="text-dark small">
                  {lead.created_on
                    ? new Date(lead.created_on).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "Recent"}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h6 className="fw-bold text-dark mb-1">Requirement Overview</h6>
            <p className="text-secondary small mb-0 lh-base" style={{ whiteSpace: "pre-wrap" }}>
              {lead.description || "No specific details provided."}
            </p>
          </div>

          <hr className="my-1 border-slate-200" />

          {/* Notes Timeline */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                <FiMessageSquare size={16} className="text-primary" />
                <span>Notes & Updates ({notes.length})</span>
              </h6>
            </div>

            {/* Add Note Form */}
            <form onSubmit={handleAddNote} className="mb-3">
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="customer-form-input"
                  placeholder="Add a new update or reminder note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  disabled={addingNote}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm px-3 flex-shrink-0 d-inline-flex align-items-center gap-1"
                  disabled={addingNote || !newNote.trim()}
                >
                  <FiPlus size={16} />
                  <span>{addingNote ? "Adding..." : "Add"}</span>
                </button>
              </div>
            </form>

            {/* Notes list */}
            {loadingNotes ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                <span className="text-muted small">Loading notes...</span>
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-4 bg-light rounded-2 text-muted small">
                No notes added to this lead yet. Keep track of conversations and updates above.
              </div>
            ) : (
              <div className="customer-notes-timeline">
                {notes.map((n) => {
                  const isEditingThis = editingNoteId === n.id;
                  const noteDate = n.created_on
                    ? new Date(n.created_on).toLocaleString("en-IN", {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null;

                  return (
                    <div key={n.id} className="customer-note-item">
                      <div className="customer-note-item__header">
                        <span className="d-flex align-items-center gap-1">
                          <FiClock size={12} />
                          <span>{noteDate || "Update"}</span>
                        </span>
                        <div className="customer-note-item__actions">
                          {!isEditingThis ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-primary p-0 text-decoration-none"
                                onClick={() => {
                                  setEditingNoteId(n.id);
                                  setEditingNoteText(n.note);
                                }}
                                title="Edit note"
                              >
                                <FiEdit2 size={13} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                                onClick={() => handleDeleteNote(n.id)}
                                title="Delete note"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>

                      {isEditingThis ? (
                        <div className="d-flex flex-column gap-2 mt-1">
                          <input
                            type="text"
                            className="customer-form-input form-control-sm"
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                          />
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-light py-0 px-2"
                              onClick={() => setEditingNoteId(null)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary py-0 px-2"
                              onClick={() => handleSaveEditNote(n)}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="customer-note-item__text">{n.note}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="customer-modal__footer">
          {onEditLead && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm px-3 d-inline-flex align-items-center gap-1 me-auto"
              onClick={() => {
                onClose();
                onEditLead(lead);
              }}
            >
              <FiEdit2 size={14} />
              <span>Edit Lead Details</span>
            </button>
          )}
          <button type="button" className="btn btn-secondary btn-sm px-4" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerLeadDetailsModal;
